/** biome-ignore-all lint/suspicious/noExplicitAny: <Fix Type later> */
import {
	AuthenticationResult,
	Configuration,
	EventMessage,
	EventType,
	PublicClientApplication,
} from "@azure/msal-browser";
import { authPersistence } from "../utils/authPersistence";

const msalConfig: Configuration = {
	auth: {
		clientId: import.meta.env.VITE_AZURE_CLIENT_ID || "",
		authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
		redirectUri: window.location.origin,
		navigateToLoginRequestUrl: true, // Navigate back to the original request URL after login
		postLogoutRedirectUri: `${window.location.origin}/login`,
	},
	cache: {
		cacheLocation: "localStorage", // This configures where your cache will be stored
		storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
		secureCookies: window.location.protocol === "https:", // Use secure cookies in production
	},
	system: {
		allowNativeBroker: false, // Disables WAM Broker
		loggerOptions: {
			loggerCallback: (
				level: number,
				message: string,
				containsPii: boolean,
			) => {
				if (containsPii) {
					return;
				}
				// Only log errors in production
				if (level === 0) {
					console.error("[MSAL Error]", message);
				}
			},
			piiLoggingEnabled: false,
			logLevel: import.meta.env.DEV ? 2 : 0, // Info in dev, errors only in prod
		},
	} as any,
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL instance
const initializeMsal = async () => {
	await msalInstance.initialize();

	// Handle redirect promise (for users returning from redirect flow)
	try {
		const response = await msalInstance.handleRedirectPromise();
		if (response?.account) {
			// Clear any logout flags if we're successfully logging in
			sessionStorage.removeItem("msal_logout_in_progress");
			msalInstance.setActiveAccount(response.account);
			authPersistence.saveAccount(response.account);
		}
	} catch (error) {
		console.error("[Auth] Error handling redirect:", error);
	}

	// Remove the logout flag if it exists (clean up from previous session)
	const justLoggedOut = sessionStorage.getItem("msal_logout_in_progress");
	if (justLoggedOut) {
		sessionStorage.removeItem("msal_logout_in_progress");
	}

	// Add event callback to handle account changes
	msalInstance.addEventCallback((event: EventMessage) => {
		if (event.eventType === EventType.LOGIN_SUCCESS) {
			const authResult = event.payload as AuthenticationResult;
			if (authResult.account) {
				msalInstance.setActiveAccount(authResult.account);
				authPersistence.saveAccount(authResult.account);
			}
		}
	});

	// Check if user is already signed in
	const accounts = msalInstance.getAllAccounts();

	if (accounts.length > 0) {
		// Set the first account as active to enable SSO
		msalInstance.setActiveAccount(accounts[0]);
		authPersistence.saveAccount(accounts[0]);
	} else {
		// No cached accounts, check our persistence
		const savedAccount = authPersistence.getSavedAccount();

		if (savedAccount?.username) {
			// Store the saved username for later use in auth initialization
			// Using a more secure approach with a Symbol to prevent easy access
			const savedUsernameSymbol = Symbol.for("__msal_saved_username");
			(window as any)[savedUsernameSymbol] = savedAccount.username;
		}
	}
};

// Export the initialization promise so it can be awaited where needed
export const msalInitPromise = initializeMsal();
